import type {
  IAssetTemplateRemoveConfirmState,
  IExperimentUsage,
  ITemplateUsageDetail,
} from '../types';
import type { CampaignStatus } from '~/types/graphql';

import {
  USED_IN_MULTIPLE_EXPERIMENTS_TOOLTIP,
  USED_IN_MULTIPLE_EXPERIMENTS_TOOLTIP_FALLBACK,
  USED_IN_OTHER_EXPERIMENT_TOOLTIP,
  USED_IN_OTHER_EXPERIMENT_TOOLTIP_FALLBACK,
} from '../constants';
import { ICampaignType } from '~/modules/campaign/types';
import { IDisableReasonKey } from '../types';

/** The reasons raised by a template already being taken by another experiment. */
type IUsedForOtherCampaignReason =
  | IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN
  | IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP;

/** The experiment holding a template: what removing it needs, plus the status that allows removal. */
export interface IUsedCampaign extends IAssetTemplateRemoveConfirmState {
  campaignStatus?: CampaignStatus;
}

/**
 * A suffix can be held by several experiments at once, so the one to act on is the one that raised
 * the reason -- not whichever usage happens to be recorded first.
 */
const findUsageDetail = ({
  usageDetails,
  reason,
}: {
  usageDetails?: ITemplateUsageDetail[];
  reason: IUsedForOtherCampaignReason;
}): ITemplateUsageDetail | undefined => {
  if (reason === IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP) {
    return usageDetails?.find((detail) => detail.isOtherGroupCampaign);
  }

  return usageDetails?.find(
    (detail) =>
      detail.isOtherSingerCampaign || detail.isOtherPriceCampaign || detail.isOtherUrlCampaign,
  );
};

// A template usage sits on the campaign type its reason names. Only price and URL tests record their
// own, since they hold the template through a record they run on rather than through a suffix.
const getFallbackCampaignType = (reason: IUsedForOtherCampaignReason): ICampaignType => {
  if (reason === IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP)
    return ICampaignType.TEMPLATE_BUNDLE;

  return ICampaignType.TEMPLATE;
};

/**
 * Every price and URL experiment holding a template, not just the one that raised the disable reason
 * — a template renders for a set of pages, and each of those can be under test by an experiment of
 * its own, which is what the card offers "Edit experiments" for.
 *
 * One experiment records a usage per variant: a price test's Variant side runs on a duplicate of the
 * Control's product that inherits the same template, and a URL test's two sides can point at pages
 * rendering the same one. So a single experiment can occupy the suffix twice and is collapsed to one
 * entry here, keeping the Control's record (the usages come Control-first) as the one a removal acts
 * on, since removing it cascades to the Variant's.
 */
export const getExperimentUsages = (usageDetails?: ITemplateUsageDetail[]): IExperimentUsage[] => {
  const usageByCampaignId = new Map<string, IExperimentUsage>();

  usageDetails?.forEach((detail) => {
    const isResourceExperiment = detail.isOtherPriceCampaign || detail.isOtherUrlCampaign;
    if (!isResourceExperiment || usageByCampaignId.has(detail.campaignId)) return;

    usageByCampaignId.set(detail.campaignId, {
      campaignId: detail.campaignId,
      campaignName: detail.campaignName,
      campaignStatus: detail.campaignStatus,
      campaignType: detail.campaignType ?? ICampaignType.PRICE,
      priceId: detail.priceId,
      urlId: detail.urlId,
      resourceName: detail.resourceName,
      productStatus: detail.productStatus,
    });
  });

  return [...usageByCampaignId.values()];
};

/**
 * The one kind of experiment holding a template. Undefined when experiments of more than one kind
 * hold it at once, which naming either of them would misread.
 */
const getSharedExperimentType = (usages: IExperimentUsage[]): ICampaignType | undefined => {
  const [first, ...rest] = usages;
  if (!first) return undefined;

  return rest.every((usage) => usage.campaignType === first.campaignType)
    ? first.campaignType
    : undefined;
};

/** Why a template held by a single other experiment can't be picked, named by that experiment's kind. */
export const getOtherExperimentTooltip = (campaignType: ICampaignType): string =>
  USED_IN_OTHER_EXPERIMENT_TOOLTIP[campaignType] ?? USED_IN_OTHER_EXPERIMENT_TOOLTIP_FALLBACK;

/** Same, for a template several experiments hold at once — named only where they are all one kind. */
export const getMultipleExperimentsTooltip = (usages: IExperimentUsage[]): string => {
  const sharedType = getSharedExperimentType(usages);

  return (
    (sharedType && USED_IN_MULTIPLE_EXPERIMENTS_TOOLTIP[sharedType]) ??
    USED_IN_MULTIPLE_EXPERIMENTS_TOOLTIP_FALLBACK
  );
};

export const getUsedCampaignByReason = ({
  usageDetails,
  reason,
}: {
  usageDetails?: ITemplateUsageDetail[];
  reason: IUsedForOtherCampaignReason;
}): IUsedCampaign => {
  const usageDetail = findUsageDetail({ usageDetails, reason });

  return {
    campaignId: usageDetail?.campaignId ?? '',
    campaignName: usageDetail?.campaignName,
    campaignStatus: usageDetail?.campaignStatus,
    campaignType: usageDetail?.campaignType ?? getFallbackCampaignType(reason),
    priceId: usageDetail?.priceId,
    urlId: usageDetail?.urlId,
  };
};
